import type { TranslateService } from '@ngx-translate/core';
import type Component from '@highcharts/dashboards/es-modules/Dashboards/Components/Component.js';
import { addEvent } from '@highcharts/dashboards/es-modules/Shared/Utilities.js';
import { PROVIDER_CONNECTOR_TYPE } from '@h20/contracts/types/api/dashboard/DashboardLayout';
import { DASHBOARD_QUERY_KEYS } from '@h20/contracts/types/api/dashboard/DashboardQueryCatalog';
import { dataBoundComponentTypes } from './dashboard-component-types';
import {
    dataBindingOptions,
    isDataBindingOption,
    rebuildDataBindingOptions,
    resolveLabels,
} from './dashboard-editable-options';
import type { DashboardQueryBinding } from './dashboard-widget.contract';
import { redrawSettingsPanel } from '../dashboard-settings-panel';
import { Dashboards } from '../dashboards-core';
import type { DashboardLayout } from '../dashboard.types';

/** Makes a widget's data binding authorable in Composer's edit mode. */
declare module '@highcharts/dashboards/es-modules/Dashboards/Components/Component.js' {
    // eslint-disable-next-line @typescript-eslint/no-empty-object-type
    interface Options extends DashboardQueryBinding {}
}

/** Declares one connector per registered query, so an authoring board offers every dataset. */
export function withAuthoringConnectors(layout: DashboardLayout): DashboardLayout {
    const declared = layout.dataPool?.connectors ?? [];
    const declaredIds = new Set(declared.map((connector) => connector.id));

    const seeded = DASHBOARD_QUERY_KEYS.filter((queryKey) => !declaredIds.has(queryKey)).map(
        (queryKey) => ({ id: queryKey, type: PROVIDER_CONNECTOR_TYPE, queryKey }) as const
    );

    return {
        ...layout,
        dataPool: { ...layout.dataPool, connectors: [...declared, ...seeded] },
    };
}

export function installDataBindingOptions(translate: TranslateService): void {
    for (const componentType of dataBoundComponentTypes()) {
        const kept = (componentType.defaultOptions.editableOptions ?? []).filter(
            (option) => !isDataBindingOption(option)
        );
        componentType.defaultOptions.editableOptions = [
            ...kept,
            ...resolveLabels(dataBindingOptions(undefined), translate),
        ];
    }

    installBindingRefresh(translate);
}

let bindingRefreshInstalled = false;

/** Keeps a cell's parameter inputs in step with the data source it names. */
function installBindingRefresh(translate: TranslateService): void {
    if (bindingRefreshInstalled) {
        return;
    }
    bindingRefreshInstalled = true;

    function refresh(this: Component): void {
        setTimeout(() => {
            if (rebuildDataBindingOptions(this.options, translate)) {
                redrawSettingsPanel(this);
            }
        }, 0);
    }

    addEvent(Dashboards.HighchartsComponent, 'update', refresh);
}
